import { styled } from '@mui/material/styles'
import { required, useTranslate } from 'ra-core'
import { BooleanInput, Labeled } from 'ra-ui-materialui'
import React, { useEffect, useMemo } from 'react'
import { TransactionService } from 'services/api/entities/accounting/transaction/types/internal/transaction-models'
import { CountryCode } from 'services/cfg/countries'
import { getCountry } from 'services/geo-management'
import { noDecimals } from 'services/utils'
import { GetSourceFn } from 'types/common'
import LabeledNumberField from 'ui-shared/LabeledFields/LabeledNumberField'
import LabeledTextField from 'ui-shared/LabeledFields/LabeledTextField'
import KNumberInput from 'ui-shared/WrappedInputs/KNumberInput'
import { buildUseComputedRecord, CustomRecord } from './computedGetters'
import { roundTwoDecimals } from './TransactionForm'

const PREFIX = 'FormService'

const classes = {
  inline: `${PREFIX}-inline`,
  divider: `${PREFIX}-divider`,
  column: `${PREFIX}-column`,
  withoutVATLabel: `${PREFIX}-withoutVATLabel`,
  exportPadding: `${PREFIX}-exportPadding`,
  dNone: `${PREFIX}-dNone`,
}

const Root = styled('div')(({ theme }: any) => ({
  [`&.${classes.inline}`]: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
  },

  [`& .${classes.divider}`]: {
    width: '100%',
    height: '3px',
    backgroundColor: theme.palette.primary.light,
    marginTop: '20px',
  },

  [`& .${classes.column}`]: {
    display: 'flex',
    minWidth: '20%',
    margin: '0px 24px',
    flexDirection: 'column',
  },

  [`& .${classes.withoutVATLabel}`]: {
    width: '100%',
    marginTop: '10px',
  },

  [`& .${classes.exportPadding}`]: {
    padding: '10px',
  },

  [`& .${classes.dNone}`]: {
    display: 'none',
  },
}))

type ServiceComponentProps = {
  formId: number
  partialFormData: TransactionService
  getSource: GetSourceFn
  setServiceRecord: (partialRecord: CustomRecord) => void
  customerBusinessCountry?: CountryCode
}

const useComputedRecord = buildUseComputedRecord(useMemo, getCountry().code)

const FormService: React.FC<ServiceComponentProps> = ({
  formId,
  partialFormData,
  getSource,
  setServiceRecord,
  customerBusinessCountry,
}) => {
  const translate = useTranslate()
  const computedRecord = useComputedRecord(
    partialFormData,
    customerBusinessCountry
  )

  useEffect(() => {
    setServiceRecord(computedRecord as CustomRecord)
  }, [setServiceRecord, computedRecord])

  const scopedRecord = { id: formId, ...computedRecord }

  return (
    <Root className={classes.inline}>
      <div className={classes.column}>
        <h3>{translate('form.headers.revenues')}</h3>
        <LabeledNumberField
          label="form.fields.grossPrice"
          record={scopedRecord}
          source="grossPrice"
          options={roundTwoDecimals}
        />

        <LabeledNumberField
          label="form.fields.inboundVat"
          record={scopedRecord}
          source="inboundVat"
          options={roundTwoDecimals}
        />

        <LabeledNumberField
          label="form.fields.price"
          record={scopedRecord}
          source="price"
          options={roundTwoDecimals}
        />
        {partialFormData.customization && (
          <KNumberInput
            source={getSource('customization.price')}
            label="form.fields.customizationPrice"
            parse={noDecimals}
            validate={required()}
          />
        )}
      </div>

      <div className={classes.column}>
        <h3>{translate('form.headers.costs')}</h3>
        <LabeledNumberField
          label="form.fields.cost"
          record={scopedRecord}
          source="totalCost"
          options={roundTwoDecimals}
        />
        <KNumberInput
          source={getSource('costNoVat')}
          label="form.fields.costNoVat"
          validate={required()}
        />

        <KNumberInput
          source={getSource('costVatPerc')}
          label="form.fields.costVatPerc"
          validate={required()}
        />
        {partialFormData.customization && (
          <KNumberInput
            source={getSource('customization.cost')}
            label="form.fields.customizationCost"
            parse={noDecimals}
            validate={required()}
          />
        )}

        <LabeledNumberField
          label="form.fields.costVat"
          record={scopedRecord}
          source="costVat"
          options={roundTwoDecimals}
        />

        {!!partialFormData.supplier && (
          <>
            <LabeledTextField
              label="Supplier"
              record={scopedRecord}
              source="supplier"
            />

            <Labeled label="Supply Confirmation">
              <BooleanInput
                label={partialFormData.supplyConfirmed ? 'Yes' : 'No'}
                source={getSource('supplyConfirmed')}
                disabled
              />
            </Labeled>
          </>
        )}
      </div>

      <div className={classes.column}>
        <h3>{translate('form.headers.margins')}</h3>
        <LabeledNumberField
          label="form.fields.margin"
          record={scopedRecord}
          source="margin"
          options={roundTwoDecimals}
        />
        <LabeledNumberField
          label="form.fields.vatEffect"
          record={scopedRecord}
          source="vatEffect"
          options={roundTwoDecimals}
        />
        <LabeledNumberField
          label="form.fields.marginPerc"
          record={scopedRecord}
          source="marginPerc"
          options={roundTwoDecimals}
        />

        {!!partialFormData.experienceNames?.length && (
          <React.Fragment>
            <h3>Purchased Formats</h3>

            {partialFormData.experienceNames?.map((name, idx) => (
              <LabeledTextField
                key={idx}
                label="Experience"
                record={{ id: idx, name: name }}
                source="name"
              />
            ))}
          </React.Fragment>
        )}
      </div>
    </Root>
  )
}

export default FormService
